"use client";

import { useState, MouseEvent, useEffect } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import BFLLink from "@repo/shared-ui/BFLLink";
import { TradingMallExchanges } from "../../constants/TradingMallConstants";
import { handleMarketSecuritiesDL } from "./SwitchExchangeFunction";
import { selectLoginStatus, useAppSelector } from "@repo/web-store";
import { getPageType } from "@repo/shared-utils/common-utils";

const SwitchExchange = ({
  buttonText,
  nseToken,
  bseToken,
  onReady,
  loading = false,
}: {
  buttonText: { NSE: string; BSE: string };
  nseToken: number;
  bseToken: number;
  loading?: boolean;
  onReady?: (loading: boolean) => void;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const loginStatus = useAppSelector(selectLoginStatus);

  // Decide default exch based on tokens & query param
  const getDefaultExch = (): TradingMallExchanges => {
    if (nseToken === 0 && bseToken !== 0) return "BSE";
    if (bseToken === 0 && nseToken !== 0) return "NSE";
    return "NSE"; // fallback
  };

  const [exch, setExch] = useState<TradingMallExchanges>(getDefaultExch);

  // Ensure URL consistency on load
  useEffect(() => {
    if (nseToken === 0 || bseToken === 0) {
      console.warn("One of the tokens is zero, not modifying URL");
      return;
    }
    const exchParam = searchParams.get("exch");
    if (exchParam === "bse") {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("exch");
      router.replace(`${pathname}?${params.toString()}`);
    }
  }, []);

  // Hide loader when switching is done
  useEffect(() => {
    onReady?.(false);
  }, [searchParams]);

  const handleClick = (
    e: MouseEvent<HTMLSpanElement>,
    newExch: TradingMallExchanges,
  ) => {
    e.preventDefault();
    if (newExch === exch) return;

    setExch(newExch);
    onReady?.(true);

    const params = new URLSearchParams(searchParams.toString());
    if (newExch === "BSE") {
      params.set("exch", "bse");
    } else {
      params.delete("exch");
    }
    router.replace(`${pathname}?${params.toString()}`);

    // Fire DL tracking
    handleMarketSecuritiesDL(
      loginStatus,
      newExch === "NSE" ? buttonText.NSE : buttonText.BSE,
      newExch === "NSE" ? 0 : 1,
      {
        EP_CTA: newExch.toUpperCase(),
        EP_PAGE_NAME:
          (getPageType("tag-3")?.toUpperCase() ?? "") +
          " " +
          (getPageType("tag-1")?.toUpperCase() ?? ""),
      },
    );
  };

  const activeClass =
    "bg-bfl-blue text-white rounded-full inline-block py-2 px-3";
  const inactiveClass =
    "cursor-pointer bg-transparent text-bfl-blue rounded-full inline-block py-2 px-3";
  const disabledClass =
    "text-gray-500 rounded-full inline-block py-2 px-3 cursor-not-allowed";

  // helper for button class
  const getButtonClass = (
    token: number,
    currentExch: TradingMallExchanges,
    buttonExch: TradingMallExchanges,
  ) => {
    if (token === 0) return disabledClass;
    if (currentExch === buttonExch) return activeClass;
    return inactiveClass;
  };

  return (
    !loading && (
      <div className="border-bfl-blue flex h-[34px] w-[90px] items-center justify-center rounded-[30px] border text-[12px]">
        <BFLLink
          onClick={(e) => nseToken !== 0 && handleClick(e, "NSE")}
          className={getButtonClass(nseToken, exch, "NSE")}
        >
          {buttonText?.NSE}
        </BFLLink>
        <BFLLink
          onClick={(e) => bseToken !== 0 && handleClick(e, "BSE")}
          className={getButtonClass(bseToken, exch, "BSE")}
        >
          {buttonText?.BSE}
        </BFLLink>
      </div>
    )
  );
};

export default SwitchExchange;
